import useSWRInfinite from "swr/infinite"

import { FIRST_PAGE, ORDER_KW } from "../../../../../constants/generalConstants"
import { postClientProtected } from "../../../clients/clients"
import {
    IDnoSolarNotification,
    IDnoSolarNotificationsSearchResult,
    dnoSolarNotificationsMapper,
} from "../../../mappers/dnoInterface/dnoSolarNotificationsMapper"
import { APPLICATION_LIST_CONFIG } from "../../../swrConfig"
import { DNO_SOL_NOTIFICATIONS } from "../../../urls/hybrid"

const DNO_SOL_NOTIFICATIONS_PAGE_SIZE = 50
const DNO_SOL_NOTIFICATIONS_DEFAULT_ORDER = "desc"

type TGetDnoSolarNotifications = {
    data: IDnoSolarNotification[] | undefined
    isLoading: boolean
    isError: boolean
    isValidating: boolean
    downloadData: { results: unknown[] } | undefined
    page: number
    isFinalPage: boolean
    setPage: (
        size: number | ((_size: number) => number)
    ) => Promise<IDnoSolarNotificationsSearchResult[] | undefined>
    mutate: () => Promise<IDnoSolarNotificationsSearchResult[] | undefined>
}

const useGetDnoSolarNotifications = (
    searchParams: URLSearchParams
): TGetDnoSolarNotifications => {
    const ascending =
        searchParams.get(ORDER_KW) || DNO_SOL_NOTIFICATIONS_DEFAULT_ORDER
    const searchParamsLocal = new URLSearchParams(searchParams.toString())
    searchParamsLocal.delete(ORDER_KW)
    const searchParamsString = searchParamsLocal.toString()

    type TGetKey = {
        url: string
        nextPageKey: string | Record<string, string>
        pageIndex: number
    }

    const getKey = (
        pageIndex: number,
        previousPageData: IDnoSolarNotificationsSearchResult
    ): TGetKey | null => {
        const url = DNO_SOL_NOTIFICATIONS(
            searchParamsString,
            DNO_SOL_NOTIFICATIONS_PAGE_SIZE,
            ascending
        )

        if (!previousPageData)
            return { url, pageIndex, nextPageKey: FIRST_PAGE }
        const nextPageKey = previousPageData.lastEvaluatedKey
        if (!nextPageKey) return null
        return {
            url,
            pageIndex,
            nextPageKey,
        }
    }

    const {
        data: pageResults,
        error,
        isLoading,
        isValidating,
        size,
        setSize,
        mutate,
    } = useSWRInfinite(
        getKey,
        async (key: TGetKey): Promise<IDnoSolarNotificationsSearchResult> => {
            try {
                const body =
                    typeof key.nextPageKey === "string" ? null : key.nextPageKey
                const res = await postClientProtected(key.url, body)
                return dnoSolarNotificationsMapper.mapList(res)
            } catch {
                throw Error()
            }
        },
        APPLICATION_LIST_CONFIG
    )

    const isFinalPage = pageResults
        ? pageResults.some((page) => !page.lastEvaluatedKey)
        : false

    const allPageResults = pageResults
        ? pageResults.reduce(
              (
                  acc: IDnoSolarNotification[],
                  pageResult: IDnoSolarNotificationsSearchResult
              ) => [...acc, ...pageResult.notifications],
              []
          )
        : undefined

    const downloadData = pageResults
        ? pageResults.reduce(
              (
                  acc: unknown[],
                  pageResult: IDnoSolarNotificationsSearchResult
              ) => [...acc, ...pageResult.downloadData],
              []
          )
        : []

    return {
        data: allPageResults,
        downloadData: { results: downloadData },
        isLoading,
        isError: error,
        isValidating,
        page: size,
        isFinalPage,
        setPage: setSize,
        mutate,
    }
}

export default useGetDnoSolarNotifications
